import React, {useEffect, useState} from "react";
import axiosInstance from "../../utils/axiosInstance";
import {PencilSquareIcon, TrashIcon} from "@heroicons/react/24/outline";
import Loader from "../../components/Loader";
import {Link} from "react-router-dom";
import {IoArrowBackSharp} from "react-icons/io5";

const AllUsers = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [users, setUsers] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [loadingToDelete, setLoadingToDelete] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNotification, setShowNotification] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axiosInstance.get("/users");
                setUsers(response.data);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoadingToDelete(true);
            await axiosInstance.delete(`/users/${userToDelete.id}`);
            setUsers(users.filter((user) => user.id !== userToDelete.id)); // Remove user from state
            setShowDeleteModal(false); // Close modal
            setUserToDelete(null);
            setLoadingToDelete(false);

            setShowNotification(true);

            setTimeout(() => {
                setShowNotification(false);
            }, 3000);
        } catch (error) {
            setError("Failed to delete user");
        }
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "ACTIVE":
            case "YES":
                return "bg-green-100 text-green-800";
            case "DEACTIVE":
            case "NO":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Filtered users based on search term
    const filteredUsers = users.filter(
        (user) =>
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.status.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="container mx-auto p-6">
                <Loader/>
            </div>
        );
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="container mx-auto p-6">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center">
                {/* Go Back Button */}
                <button
                    onClick={() => window.history.back()} // This will navigate back to the previous page
                    className="rounded-full hover:bg-gray-200 transition-all mr-2"
                >
                    <IoArrowBackSharp size={24}/>
                </button>
                {" "}
                All Users
            </h3>

            {/* Search Bar */}
            <div className="mb-4">
                <input
                    type="text"
                    className="border border-gray-300 p-2 w-full rounded-md"
                    placeholder="Search by username, email, role, or status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <table className="min-w-full table-auto bg-white border border-gray-300 rounded-md mb-6">
                <thead>
                <tr className="bg-gray-100">
                    <th className="px-4 py-2 border-b text-left">Total Users</th>
                    <th className="px-4 py-2 border-b text-left">Total Credit Balance</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td className="px-4 py-2 border-b text-green-600 font-semibold">{users.length}</td>
                    <td className="px-4 py-2 border-b text-green-600 font-semibold">
                        {users.reduce((sum, user) => sum + (user.credit_balance || 0), 0)}
                    </td>
                </tr>
                </tbody>
            </table>

            {/* Table for displaying users */}
            <table className="min-w-full table-auto bg-white border border-gray-300 rounded-md">
                <thead>
                <tr className="bg-gray-100">
                    <th className="px-4 py-2 border-b text-left">ID</th>
                    <th className="px-4 py-2 border-b text-left">Username</th>
                    <th className="px-4 py-2 border-b text-left">Email</th>
                    <th className="px-4 py-2 border-b text-left">Country</th>
                    <th className="px-4 py-2 border-b text-left">CRS</th>
                    <th className="px-4 py-2 border-b text-left">Verified</th>
                    <th className="px-4 py-2 border-b text-left">Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredUsers.map((user, key) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border-b">{key + 1}</td>
                        <td className="px-4 py-2 border-b">{user.username}</td>
                        <td className="px-4 py-2 border-b">{user.email}</td>
                        <td className="px-4 py-2 border-b">{user.country}</td>
                        <td className="px-4 py-2 border-b">{user.credit_balance}</td>
                        <td className="px-4 py-2 border-b border-gray-300 dark:border-gray-700">
                <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                        (user.verified ? "YES" : "NO")
                    )}`}
                >
                  {user.verified ? "Yes" : "No"}
                </span>
                        </td>
                        <td className="px-4 py-2 border-b flex">
                            <button
                                className="text-yellow-500  p-1 rounded mr-2 hover:bg-yellow-600 hover:text-white flex items-center">
                                <Link to={`/admin/edit-user/${user.id}`}>
                                    <PencilSquareIcon className="h-5 w-5"/>
                                </Link>
                            </button>
                            <button
                                className="text-red-500  p-1 rounded hover:bg-red-600 hover:text-white flex items-center"
                                onClick={() => handleDeleteClick(user)}
                            >
                                <TrashIcon className="h-5 w-5"/>
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                        <h2 className="text-lg font-semibold mb-4">Are you sure?</h2>
                        <p>
                            Do you really want to delete the user{" "}
                            <strong>{userToDelete.username}</strong>? This action cannot be
                            undone.
                        </p>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                onClick={handleDeleteConfirm}
                                disabled={loadingToDelete}
                            >
                                {loadingToDelete ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Notification Popup */}
            {showNotification && (
                <div
                    className="fixed top-16 right-4 p-4 bg-blue-100 text-blue-800 border border-blue-300 rounded-md shadow-xl transition-transform transform duration-500 ease-in-out translate-y-0 opacity-100">
                    <div className="flex items-center">
                        <svg
                            className="w-6 h-6 mr-2 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4"
                            ></path>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 2a10 10 0 110 20 10 10 0 010-20z"
                            ></path>
                        </svg>
                        <p className="font-semibold">User deleted successfully!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllUsers;
